import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, setUnauthorizedHandler } from "@/lib/api";

const Ctx = createContext(null);

export function AuthProvider({ children }) {
  const [state, setState] = useState({ status: "loading", user: null, org: null, prefs: {} });
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me", {
        // Quietly: anonymous visitors will hit this on page load and 401 is expected
        validateStatus: (s) => (s >= 200 && s < 300) || s === 401,
      });
      if (!data || data?.user == null) {
        setState({ status: "anonymous", user: null, org: null, prefs: {} });
        return null;
      }
      setState({ status: "authenticated", user: data.user, org: data.org, prefs: data.preferences || {} });
      return data;
    } catch {
      setState({ status: "anonymous", user: null, org: null, prefs: {} });
      return null;
    }
  }, []);

  // boot
  useEffect(() => { refresh(); }, [refresh]);

  // wire 401 handler so the api client can sign-out on lost session
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setState({ status: "anonymous", user: null, org: null, prefs: {} });
    });
  }, []);

  const login = useCallback(async ({ email, password, totp_code }) => {
    const { data } = await api.post("/auth/login", { email, password, totp_code });
    setState({ status: "authenticated", user: data.user, org: data.org, prefs: {} });
    return data;
  }, []);

  const signup = useCallback(async (payload) => {
    const { data } = await api.post("/auth/signup", payload);
    return data;
  }, []);

  const verifyEmail = useCallback(async (token) => {
    const { data } = await api.post("/auth/verify-email", { token });
    setState({ status: "authenticated", user: data.user, org: data.org, prefs: {} });
    return data;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (_) {}
    setState({ status: "anonymous", user: null, org: null, prefs: {} });
    navigate("/signin");
  }, [navigate]);

  const updatePreferences = useCallback(async (patch) => {
    const { data } = await api.patch("/auth/preferences", patch);
    setState((s) => ({ ...s, prefs: data || {} }));
    return data;
  }, []);

  const value = useMemo(() => ({
    ...state,
    isAuthenticated: state.status === "authenticated",
    isLoading: state.status === "loading",
    login, signup, verifyEmail, logout, refresh, updatePreferences,
  }), [state, login, signup, verifyEmail, logout, refresh, updatePreferences]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth must be used inside <AuthProvider>");
  return v;
}
